import { useTranslation } from 'react-i18next';
import { armedBondToolsEqual, type ArmedBondTool } from '@peruselab/structure-editor/domain/editor';
import { BondGlyph, type BondGlyphVariant } from '../../components/BondGlyph';
import { Icon } from '../../components/Icon';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import {
  lastSpecialBondPreference,
  specialBondsOpenPreference,
  type SpecialBondKey
} from '../../preferences/displayPreferences';
import { usePreference } from '../../preferences/storedPreference';

interface BondsGroupProps {
  readonly doc: UseDrawingDocumentResult;
  // #413 -- the rail disclosures are a single-open accordion owned by ToolRail.
  readonly open: boolean;
  readonly onToggle: () => void;
}

// The Chain button is a draw *mode* (`setTool('chain')`), not an armed bond tool, so it sits in
// the slot list as its own sentinel rather than in `ArmedBondTool`.
type BondSlot =
  | { readonly kind: 'chain' }
  | {
      readonly kind: 'bond';
      readonly tool: ArmedBondTool;
      readonly glyph?: BondGlyphVariant;
      readonly icon?: 'swapHoriz' | 'bolt';
      readonly labelKey: string;
    };

// Reverse only acts on an existing wedge, wavy or dative bond, and does nothing (silently)
// anywhere else -- show what it needs while it's armed (manual-test observation #3). Ionic and
// hydrogen bonds say how they're drawn.
const HINT_KEY: Partial<Record<ArmedBondTool['kind'], string>> = {
  reverse: 'workspace.rail.bonds.hintReverse',
  ionic: 'workspace.rail.bonds.hintIonic',
  hydrogen: 'workspace.rail.bonds.hintHydrogen',
  hapto: 'workspace.rail.bonds.hintHapto',
  agostic: 'workspace.rail.bonds.hintAgostic'
};

// UI action rules (docs/ui-action-rules.md) Rule 3: every button here *arms* a bond tool; the
// drawing only changes when the user then acts on the drawingPaper (click empty space / a node to
// draw a bond of that kind, click nearer an existing bond's midpoint than either end to retype
// it). Rule 2: Single is armed at launch and nothing silently resets it. The Chain button stays a
// separate draw mode. #413 fixes the left-to-right order: Single, solid + hashed wedge, Chain,
// Double, crossed double, Triple, dative, then (ADR 0022) ionic, hydrogen, wavy, and reverse.
const BOND_SLOTS: readonly BondSlot[] = [
  { kind: 'bond', tool: { kind: 'order', order: 1 }, glyph: 'single', labelKey: 'editorToolPalette.bond.orderSingle' },
  {
    kind: 'bond',
    tool: { kind: 'wedge', style: 'solid' },
    glyph: 'solid-wedge',
    labelKey: 'editorToolPalette.bond.solidWedge'
  },
  {
    kind: 'bond',
    tool: { kind: 'wedge', style: 'hashed' },
    glyph: 'hashed-wedge',
    labelKey: 'editorToolPalette.bond.hashedWedge'
  },
  { kind: 'chain' },
  { kind: 'bond', tool: { kind: 'order', order: 2 }, glyph: 'double', labelKey: 'editorToolPalette.bond.orderDouble' },
  {
    kind: 'bond',
    tool: { kind: 'crossed-double' },
    glyph: 'crossed-double',
    labelKey: 'editorToolPalette.bond.crossedDouble'
  },
  { kind: 'bond', tool: { kind: 'order', order: 3 }, glyph: 'triple', labelKey: 'editorToolPalette.bond.orderTriple' },
  { kind: 'bond', tool: { kind: 'dative' }, glyph: 'dative', labelKey: 'editorToolPalette.bond.dative' },
  // ADR 0022 D6: ionic (the bolt), hydrogen and wavy after dative; Single / Double / Triple clear
  // a bond's marking, so there is no "clear" button.
  { kind: 'bond', tool: { kind: 'ionic' }, icon: 'bolt', labelKey: 'editorToolPalette.bond.ionic' },
  { kind: 'bond', tool: { kind: 'hydrogen' }, glyph: 'hydrogen', labelKey: 'editorToolPalette.bond.hydrogen' },
  { kind: 'bond', tool: { kind: 'wavy' }, glyph: 'wavy', labelKey: 'editorToolPalette.bond.wavy' },
  { kind: 'bond', tool: { kind: 'reverse' }, icon: 'swapHoriz', labelKey: 'editorToolPalette.bond.reverseDirection' }
];

type BondToolSlot = Extract<BondSlot, { kind: 'bond' }>;

/**
 * #1127 (owner, 2026-10-29; design on the issue): the special bonds -- rare, but chemists draw
 * them -- on a leaf that opens below the Bonds grid, in place. ADR 0024: quadruple first;
 * quintuple, sextuple, hapto and agostic bonds join it as they land.
 */
const SPECIAL_SLOTS: readonly (BondToolSlot & { readonly key: Exclude<SpecialBondKey, ''> })[] = [
  {
    kind: 'bond',
    key: 'quadruple',
    tool: { kind: 'order', order: 4 },
    glyph: 'quadruple',
    labelKey: 'editorToolPalette.bond.orderQuadruple'
  },
  {
    kind: 'bond',
    key: 'quintuple',
    tool: { kind: 'order', order: 5 },
    glyph: 'quintuple',
    labelKey: 'editorToolPalette.bond.orderQuintuple'
  },
  {
    kind: 'bond',
    key: 'sextuple',
    tool: { kind: 'order', order: 6 },
    glyph: 'sextuple',
    labelKey: 'editorToolPalette.bond.orderSextuple'
  },
  // ADR 0026 D2 (#1156): a hapto (η) bond from a metal to a ring, a bond or selected atoms.
  { kind: 'bond', key: 'hapto', tool: { kind: 'hapto' }, glyph: 'hapto', labelKey: 'editorToolPalette.bond.hapto' },
  // ADR 0026 D6 (#1157): an agostic interaction, from a C–H bond's hydrogen to a metal.
  {
    kind: 'bond',
    key: 'agostic',
    tool: { kind: 'agostic' },
    glyph: 'agostic',
    labelKey: 'editorToolPalette.bond.agostic'
  }
];

export function BondsGroup({ doc, open, onToggle }: BondsGroupProps) {
  const { t } = useTranslation();
  const { tool, setTool, armedBondTool, setArmedBondTool } = doc;

  const armBondTool = (next: ArmedBondTool) => {
    setArmedBondTool(next);
    // Arming a bond tool means "I want to draw / retype bonds now" -- leave any other pointer
    // mode, Chain included (#420: Chain must not trap the pointer -- picking another bond leaves it).
    if (tool !== 'draw') setTool('draw');
  };

  const hintKey = tool === 'chain' ? undefined : HINT_KEY[armedBondTool.kind];
  // #1127: the leaf stays as it was left; the last special bond chosen keeps a slot on the grid.
  const leafOpen = usePreference(specialBondsOpenPreference);
  const lastKey = usePreference(lastSpecialBondPreference);
  const lastSpecial = SPECIAL_SLOTS.find((slot) => slot.key === lastKey);

  const bondButton = (slot: BondToolSlot, onClick: () => void, extraClass = '', label = t(slot.labelKey)) => {
    // While Chain is the active mode, only Chain reads as active; clicking any bond tool
    // leaves Chain (armBondTool) and then this shows the newly-armed tool as pressed.
    const pressed = tool !== 'chain' && armedBondToolsEqual(armedBondTool, slot.tool);
    return (
      <button
        key={`${slot.labelKey}${extraClass}`}
        type="button"
        className={`icon-button ${pressed ? 'active' : ''} ${extraClass}`}
        aria-label={label}
        title={label}
        aria-pressed={pressed}
        onClick={onClick}
      >
        {slot.glyph ? <BondGlyph variant={slot.glyph} /> : <Icon name={slot.icon!} />}
      </button>
    );
  };

  return (
    <details id="rail-group-bonds" className="workspace-rail-bonds" open={open}>
      <summary
        onClick={(event) => {
          event.preventDefault(); // ToolRail owns which disclosure is open
          onToggle();
        }}
      >
        {t('workspace.rail.bonds.groupLabel')}
      </summary>
      <div className="workspace-rail-bonds-body" role="group" aria-label={t('workspace.rail.bonds.controlsLabel')}>
        <div className="workspace-rail-bonds-grid">
          {BOND_SLOTS.map((slot) => {
            if (slot.kind === 'chain') {
              return (
                <button
                  key="chain"
                  type="button"
                  className={`icon-button ${tool === 'chain' ? 'active' : ''}`}
                  aria-label={t('drawingToolNav.chain')}
                  title={t('drawingToolNav.chain')}
                  aria-pressed={tool === 'chain'}
                  onClick={() => setTool('chain')}
                >
                  <BondGlyph variant="chain" />
                </button>
              );
            }
            return bondButton(slot, () => armBondTool(slot.tool));
          })}
          {lastSpecial
            ? bondButton(
                lastSpecial,
                () => armBondTool(lastSpecial.tool),
                'workspace-rail-bonds-last',
                t('workspace.rail.bonds.lastSpecial', { name: t(lastSpecial.labelKey) })
              )
            : null}
          <button
            type="button"
            className={`icon-button workspace-rail-bonds-fold ${leafOpen ? 'active' : ''}`}
            aria-expanded={leafOpen}
            aria-controls="rail-special-bonds"
            aria-label={t('workspace.rail.bonds.special')}
            title={t('workspace.rail.bonds.special')}
            onClick={() => specialBondsOpenPreference.set(!leafOpen)}
          >
            <BondGlyph variant="quadruple" />
            <Icon name="expandMore" />
          </button>
        </div>
        {leafOpen ? (
          <div
            id="rail-special-bonds"
            className="workspace-rail-special-bonds"
            role="group"
            aria-label={t('workspace.rail.bonds.special')}
          >
            <p className="workspace-rail-special-bonds-title">{t('workspace.rail.bonds.special')}</p>
            <div className="workspace-rail-bonds-grid">
              {SPECIAL_SLOTS.map((slot) =>
                bondButton(slot, () => {
                  armBondTool(slot.tool);
                  lastSpecialBondPreference.set(slot.key);
                })
              )}
            </div>
          </div>
        ) : null}
        {hintKey ? <p className="workspace-rail-bonds-hint">{t(hintKey)}</p> : null}
      </div>
    </details>
  );
}
